'use client';

import { useEffect, useRef } from 'react';
import type { AnimationItem } from 'lottie-web';

type LottieRenderer = 'svg' | 'canvas' | 'html';

export interface UseLottieAnimationOptions {
  path: string;
  renderer?: LottieRenderer;
  loop?: boolean;
  autoplay?: boolean;
  rendererSettings?: Record<string, unknown>;
  /**
   * Frame range the animation initialises on. lottie-web paints frame 0
   * synchronously before any DOMLoaded handler runs, so an animation whose
   * frame 0 is blank flashes empty without this. Pass a module-level
   * constant — a fresh array each render would destroy/recreate the
   * animation (it is part of the effect's dependency list).
   */
  initialSegment?: [number, number];
  /**
   * Called once the animation instance is created.
   * Useful for wiring up scroll-driven playback or interactivity helpers.
   */
  onInstance?: (animation: AnimationItem) => void;
}

/**
 * Dynamically loads `lottie-web` on the client and creates an animation
 * for the provided container ref. Safe for Next.js SSR because the
 * lottie import only happens inside `useEffect`.
 */
export const useLottieAnimation = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  {
    path,
    renderer = 'svg',
    loop = true,
    autoplay = false,
    rendererSettings,
    initialSegment,
    onInstance,
  }: UseLottieAnimationOptions,
) => {
  const animationRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    let isMounted = true;

    void import('lottie-web').then(({ default: lottie }) => {
      if (!isMounted || !containerRef.current) {
        return;
      }

      animationRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer,
        loop,
        autoplay,
        rendererSettings,
        ...(initialSegment ? { initialSegment } : {}),
        path,
      });

      if (onInstance && animationRef.current) {
        onInstance(animationRef.current);
      }
    });

    return () => {
      isMounted = false;
      animationRef.current?.destroy();
    };
  }, [
    autoplay,
    containerRef,
    loop,
    path,
    renderer,
    rendererSettings,
    initialSegment,
    onInstance,
  ]);

  return animationRef;
};


